import { useState } from "react";
import { uploadQueued, zoneUploadNote } from "../lib/uploadQueued";
import { useQueryClient } from "@tanstack/react-query";
import { SoilSampleActions } from "../features/soil-sample-actions/SoilSampleActions";
import { SoilSampleNote } from "../features/soil-sample-note/SoilSampleNote";
import { SoilSampleKindChoice } from "../features/soil-sample-kind/SoilSampleKindChoice";
import { SoilSampleZoneResults } from "../features/soil-sample-zone-results/SoilSampleZoneResults";
import { api } from "../api";
import { ApiError } from "../api/client";
import { qk } from "../hooks/queries";
import {
  currentEvent,
  oldUploadReason,
  fmtEventDate,
} from "../lib/sampleEvents";
import { parseCsv, type ParsedCsv } from "../lib/csv";
import { labForWire } from "../lib/labTests";
import { SoilSampleLabOptions } from "../features/soil-sample-lab/SoilSampleLabOptions";
import { usePointEditStore } from "../stores/pointEditStore";
import { useZoneCreateStore } from "../stores/zoneCreateStore";
import { useLabOrder } from "../stores/labOrderStore";
import { useUiStore } from "../stores/uiStore";
import { CsvPointsMapper } from "./CsvPointsMapper";
import type { DashboardField } from "../types/contract";

// choose the sample-event kind — "Use zone samples" or "Use point samples" "Create <kind> & order
// sampling" — points: WORKS — opens the map in a point-placement session (SamplePointEditor:
// add/move/delete/renumber tools + renumber-all N→S/W→E) ending in the SS2 sampling order; zones:
// STUBBED "Upload <kind> & order sampling" — WORKS — points: CSV (column check, coordinates only)
// or GeoJSON -> SEEDS the same map point-editor session for review, then "Save and order"; zones:
// zone file upload then the SS2 order directly -> Work orders page "Upload <kind> with results"
// (WORKS) — points: CSV with lat/longs + measured values -> column auto-match + check/re-match ->
// saved as the field's soil-sample layer; zones: zone file (Z2) then the lab results file (SS7).

function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const d = e.details?.map((x) => `${x.field}: ${x.message}`).join("; ");
    return d ? `${e.message} — ${d}` : e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong.";
}

// Two different causes need two different sentences: it genuinely predates the current set, OR it
// is actually NEWER and a PIN is holding an older campaign it hid the cause the user can actually
// act on.
async function oldUploadNote(
  fieldId: string,
  savedDate: string | null | undefined,
): Promise<string> {
  const events = await api.fetchSampleEvents(fieldId).catch(() => []);
  const cur = currentEvent(events);
  if (oldUploadReason(savedDate, cur) === "pinned") {
    // Shouldn't happen under "always use the newest" — only while a LEGACY pin is still in place.
    const when = cur?.sample_date ? ` (${fmtEventDate(cur.sample_date)})` : "";
    return ` These are NEWER, but this field is still set to “${cur?.name ?? "an earlier set"}”${when}, so they’re filed under “Old samples.” Open Map Soil → Use existing samples and click “Use the newest samples” to switch.`;
  }
  return " These predate this field’s latest samples, so they’re filed under “Old samples.”";
}

export function SoilSamplesOptions({ field }: { field: DashboardField }) {
  const qc = useQueryClient();
  const lab = useLabOrder();
  const [kind, setKind] = useState<"zones" | "points" | null>(null);
  const [csvDraft, setCsvDraft] = useState<ParsedCsv | null>(null); // CSV awaiting column check/re-match
  const [orderCsvDraft, setOrderCsvDraft] = useState<ParsedCsv | null>(null); // upload-AND-ORDER CSV awaiting column check
  const [zonesAwaitingResults, setZonesAwaitingResults] = useState(false); // zones uploaded; results file next
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [noteErr, setNoteErr] = useState(false);
  const say = (msg: string, isErr = false) => {
    setNote(msg);
    setNoteErr(isErr);
    setBusy(false);
  };

  const pickKind = (k: "zones" | "points") => {
    setKind((cur) => (cur === k ? null : k));
    setCsvDraft(null);
    setZonesAwaitingResults(false);
    setNote(null);
  };

  // Uploaded point set (CSV via the column-check mapper, or GeoJSON) becomes the map
  // point-editor session — review/adjust the numbered points, then "Save and order".
  function seedEditorFromFc(fc: unknown) {
    const feats =
      (
        fc as {
          features?: { geometry?: { type?: string; coordinates?: number[] } }[];
        }
      )?.features ?? [];
    const pts = feats
      .filter(
        (f) =>
          f.geometry?.type === "Point" && Array.isArray(f.geometry.coordinates),
      )
      .map((f) => ({
        lng: f.geometry!.coordinates![0],
        lat: f.geometry!.coordinates![1],
      }));
    if (!pts.length) {
      say("No usable points found — check the coordinate columns.", true);
      return;
    }
    useZoneCreateStore.getState().end(); // sessions are mutually exclusive
    usePointEditStore.getState().start(field.field_id);
    usePointEditStore.getState().seed(pts);
    setOrderCsvDraft(null);
    setBusy(false);
    useUiStore.getState().openMap(field.field_id);
  }

  // Option 2: "Upload <kind> & order sampling".
  async function uploadAndOrder(file: File) {
    setBusy(true);
    setNote(null);
    try {
      if (kind === "zones") {
        const mint = await api.uploadFile(field.field_id, "zones", file); // Z2 — async parses & stores
        // a mint whose parse was NOT queued (job_id null) must not go on to order sampling against
        // zones that will never exist.
        if (!uploadQueued(mint))
          return say(zoneUploadNote(mint, file.name).text, true);
        await api.orderSampling(field.field_id, {
          config: {
            kind: "zones",
            lab: labForWire(useLabOrder.getState().snapshot()),
          },
        });
        void qc.invalidateQueries({ queryKey: qk.workOrders });
        void qc.invalidateQueries({
          queryKey: qk.sampleEvents(field.field_id),
        });
        setBusy(false);
        useUiStore.getState().openWorkOrders();
        return;
      }
      if (/\.csv$/i.test(file.name)) {
        const parsed = parseCsv(await file.text(), file.name);
        if (!parsed.headers.length || !parsed.rows.length) {
          return say("That CSV appears to be empty.", true);
        }
        setOrderCsvDraft(parsed);
        setBusy(false);
        return;
      }
      if (/\.(geo)?json$/i.test(file.name)) {
        seedEditorFromFc(JSON.parse(await file.text()));
        return;
      }
      say("Point sets upload as CSV (with lat/long columns) or GeoJSON.", true);
    } catch (e) {
      say(errMsg(e), true);
    }
  }

  // Option 3, first file: points = CSV (column matching) or GeoJSON; zones = the zone file.
  async function uploadWithResults(file: File) {
    setBusy(true);
    setNote(null);
    try {
      if (kind === "zones") {
        const mint = await api.uploadFile(field.field_id, "zones", file); // Z2 — async parses & stores
        if (!uploadQueued(mint))
          return say(zoneUploadNote(mint, file.name).text, true);
        setZonesAwaitingResults(true);
        return say(
          "Zones uploaded — parsing now; add the lab results file for those zones.",
        );
      }
      if (/\.csv$/i.test(file.name)) {
        // CSV with lat/longs + measured values: parse client-side, AUTO-MATCH columns
        // then the user checks/re-matches before anything saves.
        const parsed = parseCsv(await file.text(), file.name);
        if (!parsed.headers.length || !parsed.rows.length) {
          return say("That CSV appears to be empty.", true);
        }
        setCsvDraft(parsed);
        setBusy(false);
        return;
      }
      if (/\.(geo)?json$/i.test(file.name)) {
        const res = await api.saveSamplePoints(
          field.field_id,
          JSON.parse(await file.text()),
        );
        void qc.invalidateQueries({ queryKey: qk.fields });
        void qc.invalidateQueries({ queryKey: qk.samples(field.field_id) });
        void qc.invalidateQueries({ queryKey: qk.layers(field.field_id) });
        void qc.invalidateQueries({
          queryKey: qk.sampleEvents(field.field_id),
        }); // refresh the picker + old/current folders
        const oldNote =
          res.is_current === false
            ? await oldUploadNote(field.field_id, res.sample_date)
            : "";
        return say(
          `Sample points saved — the soil-samples layer for this field will update.${oldNote}`,
        );
      }
      say("Point sets upload as CSV (with lat/long columns) or GeoJSON.", true);
    } catch (e) {
      say(errMsg(e), true);
    }
  }

  // the user confirmed the CSV column mapping -> save points WITH their measured results
  // they become the field's soil-sample layer (samples + layers refresh).
  async function saveCsvPoints(result: {
    fc: unknown;
    saved: number;
    skipped: number;
  }) {
    setBusy(true);
    setNote(null);
    try {
      if (result.saved === 0) {
        return say(
          "No rows had a valid latitude/longitude — check the column matching.",
          true,
        );
      }
      const res = await api.saveSamplePoints(field.field_id, result.fc);
      void qc.invalidateQueries({ queryKey: qk.fields });
      void qc.invalidateQueries({ queryKey: qk.samples(field.field_id) });
      void qc.invalidateQueries({ queryKey: qk.layers(field.field_id) });
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(field.field_id) });
      setCsvDraft(null);
      // SS3b reports how many measured values actually landed — 0 with points saved
      // means the value columns didn't parse: warn, don't celebrate.
      if (res.results_inserted === 0) {
        return say(
          `Saved ${result.saved.toLocaleString()} points, but NO result values were recognized — re-check the property column matching.`,
          true,
        );
      }
      // Part A: if the just-saved event predates this field's current set, tell the user it landed
      // under "Old samples" so it isn't a surprise (BE-Light returns is_current from
      // v_field_sample_events).
      const oldNote =
        res.is_current === false
          ? await oldUploadNote(field.field_id, res.sample_date)
          : "";
      say(
        `Saved ${result.saved.toLocaleString()} sample points${res.results_inserted != null ? ` (${res.results_inserted.toLocaleString()} results recorded)` : " with results"}${result.skipped ? ` — skipped ${result.skipped} rows without valid coordinates` : ""} — the soil-samples layer for this field will update.${oldNote}`,
      );
    } catch (e) {
      say(errMsg(e), true);
    }
  }

  // Option 3, zones second file: the lab results for the uploaded zones (SS7). Passing the
  // zone event's sample_event_id is what flips its has_results (SS8 attributes honestly —
  // no id, no flip) — best-effort: the newest zones event at upload time.
  async function uploadZoneResults(file: File) {
    setBusy(true);
    setNote(null);
    try {
      const events = await api
        .fetchSampleEvents(field.field_id)
        .catch(() => []);
      const zoneEvent = events
        .filter((e) => e.kind === "zones")
        .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      await api.uploadFile(
        field.field_id,
        "soil-samples",
        file,
        zoneEvent ? { sample_event_id: zoneEvent.event_id } : {},
      );
      void qc.invalidateQueries({ queryKey: qk.fields });
      void qc.invalidateQueries({ queryKey: qk.samples(field.field_id) });
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(field.field_id) });
      setZonesAwaitingResults(false);
      say(
        zoneEvent
          ? `Results “${file.name}” uploaded for “${zoneEvent.name}” — processing…`
          : `Results “${file.name}” uploaded — processing… (the zone set is still being parsed; results will attach once it lands)`,
      );
    } catch (e) {
      say(errMsg(e), true);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* 1 — which kind of sample event */}
      <SoilSampleKindChoice kind={kind} onPick={pickKind} />

      {kind && (
        <SoilSampleLabOptions
          panel={lab.panel}
          onPanel={lab.setPanel}
          haney={lab.haney}
          onHaney={lab.setHaney}
        />
      )}

      {/* 2 — the three options */}
      {kind && (
        <SoilSampleActions
          kind={kind}
          fieldId={field.field_id}
          busy={busy}
          onUploadOrder={(file) => void uploadAndOrder(file)}
          onUploadWithResults={(file) => void uploadWithResults(file)}
        />
      )}

      {/* points CSV: column check/re-match before anything saves */}
      {kind === "points" && csvDraft && (
        <CsvPointsMapper
          parsed={csvDraft}
          onSave={(r) => void saveCsvPoints(r)}
          onCancel={() => setCsvDraft(null)}
        />
      )}

      {/* upload-AND-ORDER points CSV: same column check; confirming seeds the map editor */}
      {kind === "points" && orderCsvDraft && (
        <CsvPointsMapper
          parsed={orderCsvDraft}
          saveLabel="Use these points"
          onSave={(r) => seedEditorFromFc(r.fc)}
          onCancel={() => setOrderCsvDraft(null)}
        />
      )}

      {/* zones-with-results: the second file (lab results for the uploaded zones) */}
      {kind === "zones" && zonesAwaitingResults && (
        <SoilSampleZoneResults
          busy={busy}
          onUpload={(file) => void uploadZoneResults(file)}
        />
      )}

      {note && <SoilSampleNote note={note} error={noteErr} />}
    </div>
  );
}
